import React, { useState } from 'react';
import {
  HelpCircle,
  X,
  BookOpen,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  ArrowRight,
  Layers,
  Wheat,
  Tag,
  Scale,
  BarChart3,
  Database,
  Users,
  Receipt,
  FolderLock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ActionTooltip } from './ActionTooltip';

export type HelpScreenKey =
  | 'dashboard'
  | 'cycles'
  | 'feed'
  | 'sales'
  | 'settlements'
  | 'ledger'
  | 'reports'
  | 'documents'
  | 'master_data'
  | 'users'
  | 'review_resolve';

interface ScreenHelpContent {
  title: string;
  subtitle: string;
  icon: React.ElementType;
  overview: string;
  workflowSteps: Array<{ title: string; desc: string }>;
  keyRules: string[];
  coaNotes?: Array<{ code: string; name: string; note: string }>;
}

const HELP_DATA: Record<HelpScreenKey, ScreenHelpContent> = {
  dashboard: {
    title: 'Operations Home Dashboard',
    subtitle: 'Daily farm operations management and real-time field activities',
    icon: Lightbulb,
    overview:
      'The Operations Home screen provides field operators and management with immediate visibility over live swine inventory, pending field activities, and recent farm operations.',
    workflowSteps: [
      {
        title: '1. Swine Inventory Tracking',
        desc: 'Review total head in system, farm allocations, and mortality statistics in real-time.',
      },
      {
        title: '2. Urgent Operational Alerts',
        desc: 'Act upon low feed inventory warnings, unassigned cycles, and pending delivery tickets.',
      },
      {
        title: '3. Quick Action Hub',
        desc: 'Directly launch feed delivery logging, sales scale tickets, or new batch arrivals with 1-click shortcuts.',
      },
    ],
    keyRules: [
      'Inventory head counts dynamically decrement upon scale sales lifts or recorded mortalities.',
      'Active cycles must be assigned a valid farm location and grower partner.',
    ],
  },
  documents: {
    title: 'Centralized Document Repository',
    subtitle: 'Private audit attachments stored in vsb-attachments Supabase bucket',
    icon: FolderLock,
    overview:
      'The Document Repository provides an organized, audit-compliant record of all photographic scale tickets, supplier invoices, delivery slips, lab results, and settlement PDFs attached across all farm operations.',
    workflowSteps: [
      {
        title: '1. Document Search & Classification',
        desc: 'Search files by filename, classification (weighing sheet, invoice, settlement PDF), or associated entity type.',
      },
      {
        title: '2. Secure Short-Lived Access',
        desc: 'Click View to generate a short-lived authenticated URL to inspect private attachment documents and field scans.',
      },
      {
        title: '3. Audit-Preserving Soft-Void',
        desc: 'Soft-void mistaken or replaced attachments with a mandatory audit reason without deleting the underlying physical file.',
      },
    ],
    keyRules: [
      'Documents are securely stored in private storage and never exposed publicly.',
      'Soft-voided documents remain permanently logged with timestamp and author for historical audit compliance.',
    ],
  },
  cycles: {
    title: 'Production Cycles & Herd Operations',
    subtitle: 'Grow-out lifecycle, animal inventory, and cost tracking',
    icon: Layers,
    overview:
      'Manages the operational lifecycle of all swine batches across Contract Growers and Company-Owned production units.',
    workflowSteps: [
      {
        title: '1. Batch Creation & Pig Arrival',
        desc: 'Initiate a cycle with batch code and raiser attribution. Record piglet arrival (Account 3010) with headcount and delivery receipt.',
      },
      {
        title: '2. Grow-out & Mortality Logging',
        desc: 'Feed deliveries and health treatments allocate directly to the cycle. Log mortality events (Account 2010) promptly to maintain live head balances.',
      },
      {
        title: '3. Cycle Status Progression',
        desc: 'Follow the approved 3-state lifecycle: Active (including sales and draft settlement) -> Settled (frozen settlement) -> Closed.',
      },
    ],
    keyRules: [
      'A cycle remains in Active status while pigs are on feed, during ongoing sales, and while a settlement is in Draft.',
      'A cycle transitions to Settled only after the grower settlement is finalized and frozen.',
      'Company-owned cycles (e.g. VSB Farm Anus) do not receive grower settlements.',
    ],
    coaNotes: [
      { code: '3010', name: 'Piglets', note: 'Base piglet delivery cost booked to cycle' },
      { code: '3012', name: 'Piglets - Trucking', note: 'Piglet hauling and transport' },
      { code: '2010', name: 'Mortality', note: 'Operational death event (₱0 financial debit)' },
    ],
  },
  feed: {
    title: 'Feed Management & Transfers',
    subtitle: 'Direct supplier allocation and cycle-to-cycle inventory transfers',
    icon: Wheat,
    overview:
      'Directly allocates feed from suppliers to production cycles, supports multi-cycle bulk invoices, and handles cycle-to-cycle feed transfers with exact cost and lineage preservation.',
    workflowSteps: [
      {
        title: '1. Supplier -> Cycle Delivery',
        desc: 'Record incoming supplier invoices. Multiple cycle allocation lines can be booked under a single supplier invoice document.',
      },
      {
        title: '2. Cycle -> Cycle Transfers',
        desc: 'Transfer unused feed between cycles. Source cycle cost is credited and destination cycle cost is debited with zero change in total farm feed cost.',
      },
      {
        title: '3. Lineage Preservation',
        desc: 'Transfers preserve the original supplier, invoice reference, and feed unit pricing across all movements.',
      },
    ],
    keyRules: [
      'Bodega intermediary warehouse has been removed from normal operating workflows.',
      'Mill-mixed medication delivered with feed is booked in the Feed workflow under Account 3020.',
      'Directly administered medications (injectables, vaccines) are booked in the Health workflow under Accounts 3030..3034.',
    ],
    coaNotes: [
      { code: '3020', name: 'Feeds', note: 'Feed products and mill-mixed medications' },
      { code: '3021', name: 'Feeds Mark-up', note: 'Contract grower feed markup fee' },
      { code: '3040', name: 'Trucking - Feeds', note: 'Feed freight and hauling expenses' },
    ],
  },
  sales: {
    title: 'Pig Sales & Scale Verification',
    subtitle: 'Harvest logging, scale weighment, and buyer attribution',
    icon: Tag,
    overview:
      'Records final harvest sales, net weight measurements, buyer assignment, and gross sales revenue for production cycles.',
    workflowSteps: [
      {
        title: '1. Scale Ticket Recording',
        desc: 'Log individual scale tickets with head count, gross weight, tare, and net weight in kg.',
      },
      {
        title: '2. Buyer & Price Assignment',
        desc: 'Select buyer and apply agreed price per kg or contract rate rule to compute gross sales revenue.',
      },
      {
        title: '3. Batch Sales Aggregation',
        desc: 'Multiple sale records and scale tickets can be booked against a single cycle during harvest.',
      },
    ],
    keyRules: [
      'Historical sales with unrecorded customer names remain null/blank without synthetic buyer attribution.',
      'Gross sales revenue is booked to Account 1010 (Pig Sales).',
    ],
    coaNotes: [
      { code: '1010', name: 'Pig Sales', note: 'Authoritative commercial pig harvest revenue' },
      { code: '1011', name: 'Pig Sales - Others', note: 'Culls and miscellaneous animal sales' },
    ],
  },
  settlements: {
    title: 'Grower Settlements Workspace',
    subtitle: 'Contract grower profit-sharing calculations and finalization',
    icon: Scale,
    overview:
      'Calculates grower profit split based strictly on the explicit contract attached to the Production Cycle (without assuming any default split), applies post-profit adjustments (used sacks, cash advances, retail sales), and finalizes grower net payouts.',
    workflowSteps: [
      {
        title: '1. Draft Settlement Creation',
        desc: 'Contract-grower cycles with recorded sales automatically generate one Draft settlement.',
      },
      {
        title: '2. Review Gross Margin & Contract Split',
        desc: 'Verify sales revenue against total production costs (piglets, feed, medication, farm expenses) to compute gross profit, then apply the contractually specified grower share rate rule attached to the cycle.',
      },
      {
        title: '3. Adjustments & Freezing',
        desc: 'Add grower adjustments (Accounts 4010..4050). Once reviewed, freeze settlement to lock payout and transition cycle to Settled.',
      },
    ],
    keyRules: [
      'Grower profit split is determined solely by the explicit contract attached to the Production Cycle — no default split is assumed.',
      'Cycles without sales have no settlement record.',
      'Company-owned cycles (e.g. VSB Farm Anus) have no grower settlement.',
      'Once frozen, settlement figures and cycle status cannot be modified.',
    ],
    coaNotes: [
      { code: '4010', name: 'Retail Sales Raiser', note: 'Deduction for live pigs sold locally by grower' },
      { code: '4020', name: 'Used Sacks', note: 'Credit to grower for returned feed sacks' },
      { code: '4030', name: 'Cash Advance', note: 'Deduction for cash advances issued to grower' },
      { code: '4050', name: "Raiser's Expense", note: 'Deduction for raiser farm maintenance expenses' },
    ],
  },
  ledger: {
    title: 'General Ledger (Book of Accounts)',
    subtitle: 'Authoritative 13-column transactional book matching Excel structure',
    icon: Receipt,
    overview:
      'Provides the complete, audit-ready General Ledger with all 2,705 baseline rows and live database-backed operational transactions.',
    workflowSteps: [
      {
        title: '1. 13-Column Excel Alignment',
        desc: 'Columns mirror Excel: Date, Raiser, Batch, Supplier, Customer, Ledger Nr, Ledger Description, Invoice Ref #, Description, Qty, Qty Kg, Price, Total.',
      },
      {
        title: '2. Real-time Search & Filtering',
        desc: 'Filter across all 2,705 transactions by batch, Chart of Accounts code (1010–4030), supplier, customer, or date range.',
      },
      {
        title: '3. Operational Drilldown',
        desc: 'Click on any ledger entry to view complete physical measurements, document references, and jump to the cycle workspace.',
      },
    ],
    keyRules: [
      'Unfiltered baseline reconciles exactly to 2,705 source rows and ₱221,227,307.60 volume.',
      'Account 1010 Pig Sales contains all 377 authoritative sales rows and ₱108,554,165.60.',
      'Future 2027+ transactions automatically appear dynamically without regenerating static files.',
    ],
  },
  reports: {
    title: 'Reports & Operational Analytics',
    subtitle: 'Executive analytics, Monthly Financial Activity, and performance comparison',
    icon: BarChart3,
    overview:
      'Delivers comprehensive herd analytics, monthly calendar cashflow activity, and comparative multi-cycle performance reports.',
    workflowSteps: [
      {
        title: '1. Management Summary View',
        desc: 'High-level KPI cards (Pig Sales Revenue, Production Costs, Net Movement, Closed Gross Margin %, Pigs Sold, Feed Allocated) and batch charts.',
      },
      {
        title: '2. Production Performance View',
        desc: 'Direct cycle debits, revenue, and gross profit breakdown with View by Production Cycle and View by Raiser modes.',
      },
      {
        title: '3. Monthly Financial Activity View',
        desc: 'Monthly calendar cashflow ledger activity with Account breakdown, MoM/YoY growth badges, and annual comparative summaries.',
      },
    ],
    keyRules: [
      'Interim weight checks, ADG, and FCR are removed from active reporting flows.',
      'Monthly financial statements roll back seamlessly to the General Ledger totals.',
    ],
  },
  master_data: {
    title: 'Master Data Management',
    subtitle: 'Canonical reference entities, products, and contract rate rules',
    icon: Database,
    overview:
      'Manage canonical master reference data including Growers, Buyers, Suppliers, Feed Products, Medicine Products, and approved Rate Rules.',
    workflowSteps: [
      {
        title: '1. Entity Navigation',
        desc: 'Switch between master entity tabs to view, search, and filter records.',
      },
      {
        title: '2. Create & Edit Records',
        desc: 'Add new growers, suppliers, or products with complete contact and code metadata.',
      },
      {
        title: '3. Status & Archive Management',
        desc: 'Toggle active status to safely archive outdated suppliers or inactive contract rules.',
      },
    ],
    keyRules: [
      'Grower codes and product codes must remain unique.',
      'Historical master records must not be deleted to preserve transactional lineage.',
    ],
  },
  users: {
    title: 'Users & Modular Permissions',
    subtitle: 'User access control, invitation provisioning, and JSONB module permission matrix',
    icon: Users,
    overview:
      'Administer system user accounts, invite new users via email, manage roles (Admin, Operations, Viewer), and customize fine-grained module-level permission checkboxes.',
    workflowSteps: [
      {
        title: '1. Invite New User',
        desc: 'Click "+ Add User" to send an email invitation with Full Name, Email, and System Role. The invited user sets their own password upon first login.',
      },
      {
        title: '2. Profile Oversight & Status',
        desc: 'View all registered user profiles, contact information, and current activation status.',
      },
      {
        title: '3. Module-Level Permission Customization',
        desc: 'Click "Configure" on any user profile to customize permissions across Cycles, Feed, Sales, Settlements, Ledger, and Master Data.',
      },
      {
        title: '4. Admin Unrestricted Access',
        desc: 'Admin users possess unrestricted access across all modules regardless of individual checkboxes.',
      },
    ],
    keyRules: [
      'Only active Admins can invite new users, change roles, or modify permission matrices.',
      'Permissions are enforced both on route navigation and interactive action buttons.',
    ],
  },
  review_resolve: {
    title: 'Review & Resolve Queue',
    subtitle: 'Administrative Triage & Reconciliation',
    icon: AlertTriangle,
    overview:
      'Native Administration queue for resolving genuine historical ambiguities, compound raiser names, unverified rate rules, and duplicate transaction candidates.',
    workflowSteps: [
      {
        title: '1. Needs Review Triage',
        desc: 'Inspect active ambiguities flagged during ingestion or operations requiring human judgment and documentation.',
      },
      {
        title: '2. Interactive Decision Modals',
        desc: 'Resolve compound grower names, link piglet placement sources, confirm counterparties, and verify duplicate candidates.',
      },
      {
        title: '3. Reversible Audit Logs',
        desc: 'View resolved decision logs with timestamps, administrator audit notes, and 1-click decision reopening capability.',
      },
    ],
    keyRules: [
      'Only Admins and authorized users can apply or reopen reconciliation decisions.',
      'Original imported Excel strings and lineage rows remain permanently immutable.',
      'Decoupled piglet placement does not alter contract grower profit-sharing relationships.',
    ],
  },
};

interface ScreenHelpDrawerProps {
  screenKey: HelpScreenKey;
}

export const ScreenHelpDrawer: React.FC<ScreenHelpDrawerProps> = ({ screenKey }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const help = HELP_DATA[screenKey] || HELP_DATA.dashboard;
  const Icon = help.icon;

  return (
    <>
      {/* Help Drawer Trigger Button */}
      <ActionTooltip content="Open in-app operational guide, business rules, and Chart of Accounts for this screen.">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(true)}
          className="text-xs font-semibold border-slate-300 hover:bg-slate-50 text-slate-700 h-8 gap-1.5 print:hidden shadow-2xs"
        >
          <HelpCircle className="w-3.5 h-3.5 text-emerald-700" />
          <span className="hidden sm:inline">Screen Help</span>
        </Button>
      </ActionTooltip>

      {/* Slide-over Drawer */}
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden print:hidden animate-in fade-in duration-200">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-2xs transition-opacity"
            onClick={() => setIsOpen(false)}
          />

          <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
            <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col z-50">
              {/* Header */}
              <div className="p-4 bg-slate-900 text-white flex items-center justify-between border-b border-slate-800">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-emerald-700 text-white flex items-center justify-center font-bold">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold tracking-tight">{help.title}</h2>
                    <p className="text-[10px] text-emerald-400 font-medium uppercase">Operational Guide</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition"
                  aria-label="Close help drawer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content Body */}
              <div className="flex-1 overflow-y-auto p-5 space-y-6 text-xs text-slate-700 no-scrollbar">
                {/* Overview Box */}
                <div className="p-3.5 bg-emerald-50/70 border border-emerald-200 rounded-lg space-y-1">
                  <span className="font-bold text-emerald-950 block text-[11px] uppercase tracking-wider">
                    Overview
                  </span>
                  <p className="text-emerald-900 leading-relaxed">{help.overview}</p>
                </div>

                {/* Workflow Steps */}
                <div className="space-y-3">
                  <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider flex items-center gap-1.5">
                    <BookOpen className="w-3.5 h-3.5 text-slate-700" />
                    <span>Operational Workflow</span>
                  </h3>
                  <div className="space-y-2.5">
                    {help.workflowSteps.map((step, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1">
                        <span className="font-bold text-slate-800 text-[11px] block">{step.title}</span>
                        <p className="text-slate-600 leading-relaxed">{step.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Key Rules */}
                <div className="space-y-2.5">
                  <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                    <span>Governing Rules</span>
                  </h3>
                  <ul className="space-y-1.5">
                    {help.keyRules.map((rule, idx) => (
                      <li key={idx} className="flex items-start gap-2 bg-white p-2 border border-slate-100 rounded-md shadow-2xs">
                        <ArrowRight className="w-3 h-3 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 leading-snug">{rule}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* COA Notes (if applicable) */}
                {help.coaNotes && help.coaNotes.length > 0 && (
                  <div className="space-y-2.5">
                    <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider flex items-center gap-1.5">
                      <Receipt className="w-3.5 h-3.5 text-slate-700" />
                      <span>Chart of Accounts Classification</span>
                    </h3>
                    <div className="divide-y divide-slate-200 border border-slate-200 rounded-lg overflow-hidden font-mono text-[11px]">
                      {help.coaNotes.map((c) => (
                        <div key={c.code} className="p-2.5 bg-slate-50/70 flex items-start gap-2">
                          <span className="font-bold px-1.5 py-0.5 bg-slate-200 text-slate-800 rounded font-sans text-[10px]">
                            {c.code}
                          </span>
                          <div>
                            <span className="font-bold text-slate-800 font-sans block text-xs">{c.name}</span>
                            <span className="text-slate-600 font-sans text-[10px]">{c.note}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
                <span className="text-[11px] text-slate-500">VSB Farm Management System • WP13B</span>
                <Button size="sm" variant="outline" onClick={() => setIsOpen(false)} className="text-xs">
                  Close Help
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
